import { useState, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useContent } from "@/hooks/useData";
import { api, formatApiErrorDetail } from "@/lib/api";
import { AdminHeader } from "@/components/admin/AdminUI";
import { ImageUpload } from "@/components/admin/ImageUpload";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";

const TInput = ({ label, value, onChange, testid }) => (
  <div className="space-y-1.5">
    <Label className="label-eyebrow text-[#5E5A54]">{label}</Label>
    <Input value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="rounded-none bg-white border-[#ECEAE4]" data-testid={testid} />
  </div>
);

const TArea = ({ label, value, onChange, rows = 3, testid }) => (
  <div className="space-y-1.5">
    <Label className="label-eyebrow text-[#5E5A54]">{label}</Label>
    <Textarea rows={rows} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="rounded-none bg-white border-[#ECEAE4]" data-testid={testid} />
  </div>
);

const ListEditor = ({ items = [], fields, onChange, addLabel }) => {
  const update = (idx, key, val) => onChange(items.map((it, i) => (i === idx ? { ...it, [key]: val } : it)));
  const add = () => onChange([...items, Object.fromEntries(fields.map((f) => [f.key, ""]))]);
  const remove = (idx) => onChange(items.filter((_, i) => i !== idx));
  return (
    <div className="space-y-4">
      {items.map((it, idx) => (
        <div key={idx} className="border border-[#ECEAE4] bg-[#F6F5F2] p-4 space-y-3 relative">
          <button type="button" onClick={() => remove(idx)} className="absolute top-3 right-3 text-[#5E5A54] hover:text-red-700" data-testid={`list-remove-${idx}`}>
            <Trash2 size={15} />
          </button>
          {fields.map((f) =>
            f.area ? (
              <TArea key={f.key} label={f.label} value={it[f.key]} onChange={(v) => update(idx, f.key, v)} rows={2} />
            ) : (
              <TInput key={f.key} label={f.label} value={it[f.key]} onChange={(v) => update(idx, f.key, v)} />
            )
          )}
        </div>
      ))}
      <Button type="button" variant="outline" onClick={add} className="rounded-none border-[#ECEAE4] label-eyebrow text-[10px]" data-testid="list-add">
        <Plus size={14} className="mr-1" /> {addLabel}
      </Button>
    </div>
  );
};

const Panel = ({ children }) => <div className="bg-white border border-[#ECEAE4] p-6 sm:p-8 space-y-5 mt-6">{children}</div>;

export default function ContentEditor() {
  const { content } = useContent();
  const qc = useQueryClient();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (content && !form) setForm(JSON.parse(JSON.stringify(content)));
  }, [content, form]);

  if (!form) return <p className="text-[#5E5A54] font-light">Loading…</p>;

  const sec = (s) => form[s] || {};
  const setF = (s, k, v) => setForm((f) => ({ ...f, [s]: { ...f[s], [k]: v } }));
  const setArr = (s, k, v) => setForm((f) => ({ ...f, [s]: { ...f[s], [k]: v } }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/content", form);
      qc.invalidateQueries({ queryKey: ["content"] });
      toast.success("Site content updated");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const TABS = ["Hero", "About", "Why", "Who", "Process", "Advantages", "FAQ", "CTA", "About Page", "Services Page", "Contact Page"];

  return (
    <div data-testid="content-editor">
      <div className="flex items-start justify-between">
        <AdminHeader eyebrow="Site Content" title="Content Editor" />
        <Button onClick={save} disabled={saving} className="rounded-none bg-[#2C2A29] hover:bg-[#4A4641] text-white h-11 px-8 label-eyebrow text-[10px]" data-testid="content-save">
          {saving ? "Saving…" : "Save Changes"}
        </Button>
      </div>

      <Tabs defaultValue="Hero" className="w-full">
        <TabsList className="flex flex-wrap h-auto bg-[#F6F5F2] rounded-none p-1 gap-1 justify-start">
          {TABS.map((t) => (
            <TabsTrigger key={t} value={t} className="rounded-none label-eyebrow text-[10px] data-[state=active]:bg-[#2C2A29] data-[state=active]:text-white px-4 py-2" data-testid={`content-tab-${t.toLowerCase().replace(/\s+/g, "-")}`}>
              {t}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="Hero">
          <Panel>
            <TInput label="Eyebrow" value={sec("hero").eyebrow} onChange={(v) => setF("hero", "eyebrow", v)} />
            <div className="grid sm:grid-cols-3 gap-4">
              <TInput label="Title line 1" value={sec("hero").title_line1} onChange={(v) => setF("hero", "title_line1", v)} />
              <TInput label="Accent word" value={sec("hero").title_accent} onChange={(v) => setF("hero", "title_accent", v)} />
              <TInput label="Title suffix" value={sec("hero").title_suffix} onChange={(v) => setF("hero", "title_suffix", v)} />
            </div>
            <TArea label="Subtitle" value={sec("hero").subtitle} onChange={(v) => setF("hero", "subtitle", v)} />
            <div className="grid sm:grid-cols-2 gap-4">
              <TInput label="Primary button" value={sec("hero").primary_cta} onChange={(v) => setF("hero", "primary_cta", v)} />
              <TInput label="Secondary button" value={sec("hero").secondary_cta} onChange={(v) => setF("hero", "secondary_cta", v)} />
              <TInput label="Trusted label" value={sec("hero").trusted_label} onChange={(v) => setF("hero", "trusted_label", v)} />
              <TInput label="Trusted countries" value={sec("hero").trusted_text} onChange={(v) => setF("hero", "trusted_text", v)} />
            </div>
            <ImageUpload label="Hero image" value={sec("hero").image_url} onChange={(v) => setF("hero", "image_url", v)} />
            <TInput label="Image caption" value={sec("hero").image_caption} onChange={(v) => setF("hero", "image_caption", v)} />
          </Panel>
        </TabsContent>

        <TabsContent value="About">
          <Panel>
            <TInput label="Eyebrow" value={sec("about").eyebrow} onChange={(v) => setF("about", "eyebrow", v)} />
            <div className="grid sm:grid-cols-3 gap-4">
              <TInput label="Title" value={sec("about").title} onChange={(v) => setF("about", "title", v)} />
              <TInput label="Accent word" value={sec("about").accent} onChange={(v) => setF("about", "accent", v)} />
              <TInput label="Title suffix" value={sec("about").title_suffix} onChange={(v) => setF("about", "title_suffix", v)} />
            </div>
            <TArea label="Paragraph 1" value={sec("about").paragraph1} onChange={(v) => setF("about", "paragraph1", v)} />
            <TArea label="Paragraph 2" value={sec("about").paragraph2} onChange={(v) => setF("about", "paragraph2", v)} />
            <TInput label="Link label" value={sec("about").cta_label} onChange={(v) => setF("about", "cta_label", v)} />
            <div className="grid sm:grid-cols-2 gap-4">
              <ImageUpload label="Image 1" value={sec("about").image1} onChange={(v) => setF("about", "image1", v)} />
              <ImageUpload label="Image 2" value={sec("about").image2} onChange={(v) => setF("about", "image2", v)} />
            </div>
          </Panel>
        </TabsContent>

        <TabsContent value="Why">
          <Panel>
            <TInput label="Eyebrow" value={sec("why").eyebrow} onChange={(v) => setF("why", "eyebrow", v)} />
            <div className="grid sm:grid-cols-2 gap-4">
              <TInput label="Title" value={sec("why").title} onChange={(v) => setF("why", "title", v)} />
              <TInput label="Accent" value={sec("why").accent} onChange={(v) => setF("why", "accent", v)} />
            </div>
            <TArea label="Description" value={sec("why").description} onChange={(v) => setF("why", "description", v)} />
            <ListEditor items={sec("why").items} onChange={(v) => setArr("why", "items", v)} addLabel="Add item" fields={[{ key: "title", label: "Title" }, { key: "description", label: "Description", area: true }]} />
          </Panel>
        </TabsContent>

        <TabsContent value="Who">
          <Panel>
            <TInput label="Eyebrow" value={sec("who").eyebrow} onChange={(v) => setF("who", "eyebrow", v)} />
            <div className="grid sm:grid-cols-2 gap-4">
              <TInput label="Title" value={sec("who").title} onChange={(v) => setF("who", "title", v)} />
              <TInput label="Accent" value={sec("who").accent} onChange={(v) => setF("who", "accent", v)} />
            </div>
            <ListEditor items={sec("who").items} onChange={(v) => setArr("who", "items", v)} addLabel="Add audience" fields={[{ key: "title", label: "Title" }, { key: "description", label: "Description", area: true }]} />
          </Panel>
        </TabsContent>

        <TabsContent value="Process">
          <Panel>
            <TInput label="Eyebrow" value={sec("process").eyebrow} onChange={(v) => setF("process", "eyebrow", v)} />
            <div className="grid sm:grid-cols-2 gap-4">
              <TInput label="Title" value={sec("process").title} onChange={(v) => setF("process", "title", v)} />
              <TInput label="Accent" value={sec("process").accent} onChange={(v) => setF("process", "accent", v)} />
            </div>
            <TArea label="Description" value={sec("process").description} onChange={(v) => setF("process", "description", v)} />
            <ListEditor items={sec("process").steps} onChange={(v) => setArr("process", "steps", v)} addLabel="Add step" fields={[{ key: "title", label: "Step title" }, { key: "description", label: "Description", area: true }]} />
          </Panel>
        </TabsContent>

        <TabsContent value="Advantages">
          <Panel>
            <TInput label="Eyebrow" value={sec("advantages").eyebrow} onChange={(v) => setF("advantages", "eyebrow", v)} />
            <div className="grid sm:grid-cols-2 gap-4">
              <TInput label="Title" value={sec("advantages").title} onChange={(v) => setF("advantages", "title", v)} />
              <TInput label="Accent" value={sec("advantages").accent} onChange={(v) => setF("advantages", "accent", v)} />
            </div>
            <ListEditor items={sec("advantages").items} onChange={(v) => setArr("advantages", "items", v)} addLabel="Add advantage" fields={[{ key: "title", label: "Title" }, { key: "description", label: "Description", area: true }]} />
          </Panel>
        </TabsContent>

        <TabsContent value="FAQ">
          <Panel>
            <TInput label="Eyebrow" value={sec("faq").eyebrow} onChange={(v) => setF("faq", "eyebrow", v)} />
            <div className="grid sm:grid-cols-2 gap-4">
              <TInput label="Title" value={sec("faq").title} onChange={(v) => setF("faq", "title", v)} />
              <TInput label="Accent" value={sec("faq").accent} onChange={(v) => setF("faq", "accent", v)} />
            </div>
            <ListEditor items={sec("faq").items} onChange={(v) => setArr("faq", "items", v)} addLabel="Add question" fields={[{ key: "q", label: "Question" }, { key: "a", label: "Answer", area: true }]} />
          </Panel>
        </TabsContent>

        <TabsContent value="CTA">
          <Panel>
            <TInput label="Eyebrow" value={sec("cta").eyebrow} onChange={(v) => setF("cta", "eyebrow", v)} />
            <div className="grid sm:grid-cols-2 gap-4">
              <TInput label="Title" value={sec("cta").title} onChange={(v) => setF("cta", "title", v)} />
              <TInput label="Accent" value={sec("cta").accent} onChange={(v) => setF("cta", "accent", v)} />
            </div>
            <TArea label="Subtitle" value={sec("cta").subtitle} onChange={(v) => setF("cta", "subtitle", v)} />
            <TInput label="Button label" value={sec("cta").button} onChange={(v) => setF("cta", "button", v)} />
            <ImageUpload label="Background image" value={sec("cta").image_url} onChange={(v) => setF("cta", "image_url", v)} />
          </Panel>
        </TabsContent>
        <TabsContent value="About Page">
          <Panel>
            <p className="label-eyebrow text-[10px] text-[#9E8773]">Page Header</p>
            <TInput label="Eyebrow" value={sec("about_page").header_eyebrow} onChange={(v) => setF("about_page", "header_eyebrow", v)} testid="aboutpage-eyebrow" />
            <div className="grid sm:grid-cols-2 gap-4">
              <TInput label="Title" value={sec("about_page").header_title} onChange={(v) => setF("about_page", "header_title", v)} />
              <TInput label="Accent" value={sec("about_page").header_accent} onChange={(v) => setF("about_page", "header_accent", v)} />
            </div>
            <TArea label="Description" value={sec("about_page").header_description} onChange={(v) => setF("about_page", "header_description", v)} />
            <p className="label-eyebrow text-[10px] text-[#9E8773] pt-4">Intro Section</p>
            <TInput label="Intro heading" value={sec("about_page").intro_heading} onChange={(v) => setF("about_page", "intro_heading", v)} />
            <TArea label="Paragraph 1" value={sec("about_page").intro_paragraph1} onChange={(v) => setF("about_page", "intro_paragraph1", v)} />
            <TArea label="Paragraph 2" value={sec("about_page").intro_paragraph2} onChange={(v) => setF("about_page", "intro_paragraph2", v)} />
            <ImageUpload label="Intro image" value={sec("about_page").intro_image} onChange={(v) => setF("about_page", "intro_image", v)} />
            <p className="label-eyebrow text-[10px] text-[#9E8773] pt-4">Promise Section</p>
            <TInput label="Eyebrow" value={sec("about_page").promise_eyebrow} onChange={(v) => setF("about_page", "promise_eyebrow", v)} />
            <div className="grid sm:grid-cols-2 gap-4">
              <TInput label="Title" value={sec("about_page").promise_title} onChange={(v) => setF("about_page", "promise_title", v)} />
              <TInput label="Accent" value={sec("about_page").promise_accent} onChange={(v) => setF("about_page", "promise_accent", v)} />
            </div>
            <TArea label="Description" value={sec("about_page").promise_description} onChange={(v) => setF("about_page", "promise_description", v)} />
            <TInput label="Button label" value={sec("about_page").promise_button} onChange={(v) => setF("about_page", "promise_button", v)} />
          </Panel>
        </TabsContent>

        <TabsContent value="Services Page">
          <Panel>
            <p className="label-eyebrow text-[10px] text-[#9E8773]">Page Header</p>
            <TInput label="Eyebrow" value={sec("services_page").header_eyebrow} onChange={(v) => setF("services_page", "header_eyebrow", v)} testid="servicespage-eyebrow" />
            <div className="grid sm:grid-cols-2 gap-4">
              <TInput label="Title" value={sec("services_page").header_title} onChange={(v) => setF("services_page", "header_title", v)} />
              <TInput label="Accent" value={sec("services_page").header_accent} onChange={(v) => setF("services_page", "header_accent", v)} />
            </div>
            <TArea label="Description" value={sec("services_page").header_description} onChange={(v) => setF("services_page", "header_description", v)} />
            <p className="label-eyebrow text-[10px] text-[#9E8773] pt-4">Service Cards</p>
            <ListEditor items={sec("services_page").items} onChange={(v) => setArr("services_page", "items", v)} addLabel="Add service" fields={[{ key: "title", label: "Title" }, { key: "description", label: "Description", area: true }]} />
            <p className="label-eyebrow text-[10px] text-[#9E8773] pt-4">Trade & Developers Section</p>
            <TInput label="Eyebrow" value={sec("services_page").trade_eyebrow} onChange={(v) => setF("services_page", "trade_eyebrow", v)} />
            <div className="grid sm:grid-cols-2 gap-4">
              <TInput label="Title" value={sec("services_page").trade_title} onChange={(v) => setF("services_page", "trade_title", v)} />
              <TInput label="Accent" value={sec("services_page").trade_accent} onChange={(v) => setF("services_page", "trade_accent", v)} />
            </div>
            <TArea label="Description" value={sec("services_page").trade_description} onChange={(v) => setF("services_page", "trade_description", v)} />
            <TInput label="Button label" value={sec("services_page").trade_button} onChange={(v) => setF("services_page", "trade_button", v)} />
          </Panel>
        </TabsContent>

        <TabsContent value="Contact Page">
          <Panel>
            <p className="label-eyebrow text-[10px] text-[#9E8773]">Page Header</p>
            <TInput label="Eyebrow" value={sec("contact_page").header_eyebrow} onChange={(v) => setF("contact_page", "header_eyebrow", v)} testid="contactpage-eyebrow" />
            <div className="grid sm:grid-cols-2 gap-4">
              <TInput label="Title" value={sec("contact_page").header_title} onChange={(v) => setF("contact_page", "header_title", v)} />
              <TInput label="Accent" value={sec("contact_page").header_accent} onChange={(v) => setF("contact_page", "header_accent", v)} />
            </div>
            <TArea label="Description" value={sec("contact_page").header_description} onChange={(v) => setF("contact_page", "header_description", v)} />
            <p className="label-eyebrow text-[10px] text-[#9E8773] pt-4">Contact Details</p>
            <div className="grid sm:grid-cols-2 gap-4">
              <TInput label="Based in" value={sec("contact_page").based_in} onChange={(v) => setF("contact_page", "based_in", v)} />
              <TInput label="Email" value={sec("contact_page").email} onChange={(v) => setF("contact_page", "email", v)} />
            </div>
            <TInput label="WhatsApp link label" value={sec("contact_page").whatsapp_label} onChange={(v) => setF("contact_page", "whatsapp_label", v)} />
          </Panel>
        </TabsContent>
      </Tabs>
    </div>
  );
}
